import Input from "Components/Form/Input";
import { FormEvent, useState } from "react";
import moment, { Moment } from "moment";
import { createToast } from "Utils/toastFunc";
import { Button } from "Components/Button";
import InputDateRange from "Components/Form/InputDateRange";
import Select from "Components/Form/Select";
import { useIsFetching, useQuery } from "react-query";
import { Multiples, preBudgetCombos } from "Requests/Partners/PreBudgets";
import { Box } from "styles/globalComponents";
import SelectSearch from "Components/Form/SelectSearch";
import { PREBUDGETSTATUS } from "Constants/PreBudget";
import { getUnitsUser } from "Requests/HomeOrder";
import { FiltersState } from "../usePreOrderList";
import { FiltersStyled, FormContainer } from "../styles";

moment.locale("pt-br");

interface FiltersProps {
  setProductFilters: (filters: FiltersState) => void;
  productsQuantity: number;
  setNewPage: (page: number) => void;
}

const initialFilters = {
  search: "",
  start_date: undefined,
  end_date: undefined,
  status_id: [{ id: PREBUDGETSTATUS.pending, status: "Pendente" }],
  client_id: null,
  state: "",
  units_id: null,
};

const STATE = [
  {
    name: "Rio de Janeiro",
    id: "RJ",
  },
  {
    name: "São Paulo",
    id: "SP",
  },
  {
    name: "Paraná",
    id: "PR",
  },
];

export const Filters = ({
  setProductFilters,
  productsQuantity,
  setNewPage,
}: FiltersProps) => {
  const [filters, setFilters] = useState<{
    search: string;
    start_date: Moment | undefined;
    end_date: Moment | undefined;
    state: string;
    status_id: Multiples | null;
    client_id: Multiples | null;
    units_id: Multiples | null;
  }>(initialFilters);

  const { data, isLoading } = useQuery({
    queryFn: preBudgetCombos,
    queryKey: ["pre-order-combo"],
    refetchOnWindowFocus: false,
  });

  const isFetching = useIsFetching({ queryKey: ["listAllPreOrder"] }) > 0;

  const units = useQuery({
    queryFn: getUnitsUser,
    queryKey: "user-units",
    refetchOnWindowFocus: false,
  });

  const hasNoFiltersSelected =
    !filters.search &&
    !filters.start_date &&
    !filters.end_date &&
    !filters.status_id &&
    !filters.client_id &&
    !filters.state;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (hasNoFiltersSelected)
      return createToast("error", "Selecione um tipo de busca");
    setNewPage(1);

    const updatedFilters = {
      ...filters,
      status_id: filters.status_id?.map((status) => status.id) || null,
      client_id: filters.client_id?.map((status) => status.id) || null,
    };

    setProductFilters(updatedFilters);
  }

  const clearFilters = () => {
    const updatedFilters = {
      ...initialFilters,
      status_id: [1],
      client_id: null,
    };
    setProductFilters(updatedFilters);
    setFilters(initialFilters);
  };

  function handleDatesChange(value: any) {
    if (value.startDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: value.startDate,
      }));
    }

    if (value.endDate) {
      setFilters((prev) => ({
        ...prev,
        end_date: value.endDate,
      }));
    }
  }

  const onSelectPrevDay = () => {
    setNewPage(1);

    const yesterdayDate = moment().subtract(1, "day");
    const tomorrowDate = moment();

    const currentFiterSate = {
      ...filters,
      search: "",
      start_date: yesterdayDate,
      end_date: tomorrowDate,
      status_id: null,
      client_id: null,
      state: "",
    };

    setFilters(currentFiterSate);

    setProductFilters({ ...currentFiterSate });
  };

  return (
    <FiltersStyled className="filters-holder">
      <div className="filters-order">
        <FormContainer className="select-search-submit" onSubmit={handleSubmit}>
          <Input
            readOnly={false}
            placeholder="Buscar por documento ou nome do paciente"
            value={filters.search}
            onChange={({ target }: any) =>
              setFilters((prev) => ({ ...prev, search: target.value }))
            }
            type="text"
            error=""
            id="search"
            description=""
            label=""
            mask=""
            maskChar=""
            maxLength={200}
            min={1}
            multiline={false}
            onBlur={() => {}}
            onFocus={() => {}}
            onKeyPress={() => {}}
            purple={false}
            required={false}
            small={false}
          />

          <div className="date-range">
            <InputDateRange
              startDatePlaceholderText="Data inicial"
              endDatePlaceholderText="Data final"
              startDateId="start"
              endDateId="end"
              onDatesChange={(value: any) => handleDatesChange(value)}
              openInModal={false}
              dates={{
                startDate: filters.start_date,
                endDate: filters.end_date,
              }}
              blockPastChanges={false}
              withClear={false}
              error={undefined}
              small={undefined}
              purple={undefined}
              label={undefined}
            />
          </div>

          <div className="state ">
            <Select
              id="state"
              placeholder="Selecione o estado"
              optionName="name"
              options={STATE}
              readOnly={isLoading}
              value={filters.state}
              onChange={({ target: { value } }: any) =>
                setFilters((prev) => ({ ...prev, state: value }))
              }
            />
          </div>

          <div className="partner multiple-selection">
            <SelectSearch
              label=""
              id="client"
              value={filters.client_id}
              error=""
              isMulti
              isMoreSelect
              options={data?.clients}
              loading={!data?.clients.length}
              readOnly={!data?.clients.length}
              valueKey="name"
              labelKey="name"
              placeholder="Parceiro"
              onChange={(value: any) => {
                if (value.id === "")
                  return setFilters((prev) => ({ ...prev, client_id: null }));
                setFilters((prev) => ({ ...prev, client_id: value }));
              }}
            />
          </div>

          <div className="status multiple-selection">
            <SelectSearch
              id="calendars_id"
              label=""
              value={filters.status_id}
              error=""
              isMulti
              isMoreSelect
              options={data?.status}
              loading={!data?.status.length}
              readOnly={!data?.status.length}
              valueKey="id"
              labelKey="status"
              placeholder="Status"
              onChange={(value: any) => {
                if (value.id === "")
                  return setFilters((prev) => ({ ...prev, status_id: null }));
                setFilters((prev) => ({ ...prev, status_id: value }));
              }}
            />
          </div>

          <div className="status multiple-selection">
            <SelectSearch
              id="units_id"
              label=""
              value={filters.units_id}
              error=""
              isMulti
              isMoreSelect
              options={units?.data}
              loading={!units?.data?.length}
              readOnly={!units?.data?.length}
              valueKey="id"
              labelKey="name"
              placeholder="Unidades"
              onChange={(value: any) => {
                if (value.id === "")
                  return setFilters((prev) => ({ ...prev, units_id: null }));
                setFilters((prev) => ({ ...prev, units_id: value }));
              }}
            />
          </div>

          <Button onClick={onSelectPrevDay}>D-1</Button>

          <Button onClick={handleSubmit} type="submit">
            {" "}
            Buscar{" "}
          </Button>
          <Button onClick={clearFilters} textButton>
            {" "}
            Limpar busca{" "}
          </Button>
        </FormContainer>

        {/* {data?.dashboards && (
          <>
            <h1 className="or text-center">OU</h1>

            <div className="fast-filtering">
              <h1>Busca rápida por status</h1>

              <div className="wrapper">
                <div className="filters">
                  <TooltipButton
                    label={data?.dashboards?.cancelled || 0}
                    tooltip="Cancelado(s)"
                    color="#ff0000"
                  />
                  <TooltipButton
                    label={data?.dashboards?.pending || 0}
                    tooltip="Pendente(s)"
                    color="#ff8000 "
                  />
                  <TooltipButton
                    label={data?.dashboards?.partial || 0}
                    tooltip="Parcial(is)"
                    color="#aaff00"
                  />
                  <TooltipButton
                    label={data?.dashboards?.confirmed || 0}
                    tooltip="Confirmado(s)"
                    color=" #20cc20 "
                  />
                </div>
                <span className="total-items">
                  Total de itens {data?.dashboards?.total}
                </span>
              </div>
            </div>
          </>
        )} */}

        {isFetching ? null : (
          <Box mt="20" display="flex" justify="end">
            <p>{productsQuantity} resultado(s) encontrado(s)</p>
          </Box>
        )}
      </div>
    </FiltersStyled>
  );
};
